import { lazy, Suspense, type ReactNode } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useBrand } from './lib/brand';
import { useRouteScroll } from './lib/useRouteScroll';
import MarketingLanding from './pages/MarketingLanding';
import CopilotPage from './marketing/CopilotPage';
import FeaturesPage from './marketing/FeaturesPage';
import PricingPage from './marketing/PricingPage';
import StoryPage from './marketing/StoryPage';
import ContactPage from './marketing/ContactPage';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';

const Login = lazy(() => import('./pages/Login'));
const AdminRoutes = lazy(() => import('./routes/AdminRoutes'));
const WascSite = lazy(() => import('./routes/WascSite'));
const AuthRoutes = lazy(() => import('./routes/AuthRoutes'));
const PoolsideRoutes = lazy(() => import('./routes/PoolsideRoutes'));
const IntakePreview = import.meta.env.DEV ? lazy(() => import('./pages/IntakePreview')) : null;
const CommunicationsPreview = import.meta.env.DEV
  ? lazy(() => import('./pages/CommunicationsPreview'))
  : null;
const NoticePeriodPreview =
  import.meta.env.DEV || import.meta.env.VITE_APP_ENV !== 'production'
    ? lazy(() => import('./pages/NoticePeriodPreview'))
    : null;
const NoticeMonthPreview =
  import.meta.env.DEV || import.meta.env.VITE_APP_ENV !== 'production'
    ? lazy(() => import('./pages/NoticeMonthPreview'))
    : null;
const WeekMeetsPreview =
  import.meta.env.DEV || import.meta.env.VITE_APP_ENV !== 'production'
    ? lazy(() => import('./pages/WeekMeetsPreview'))
    : null;

function FullPageSpinner() {
  return <div className="grid min-h-screen place-items-center text-slate-500">Loading…</div>;
}

function Deferred({ children }: { children: ReactNode }) {
  return <Suspense fallback={<FullPageSpinner />}>{children}</Suspense>;
}

/** `/wasc/...` → `/WASC/...`. Must be case-sensitive: route matching is not, so a
 *  case-blind `/wasc/*` also matches `/WASC/news/:slug` and bounces the story
 *  back to the landing page. */
function CanonicalWascPath() {
  const { pathname, search, hash } = useLocation();
  const next = pathname.replace(/^\/wasc\b/, '/WASC');
  return <Navigate to={`${next}${search}${hash}`} replace />;
}

export default function App() {
  useBrand();
  useRouteScroll('window', () => window);
  return (
    <Routes>
      <Route path="/" element={<MarketingLanding />} />
      <Route path="/coaches" element={<CopilotPage />} />
      <Route path="/co-pilot" element={<Navigate to="/coaches" replace />} />
      <Route path="/features" element={<FeaturesPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/story" element={<StoryPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/marketing" element={<Navigate to="/" replace />} />
      <Route path="/wasc/*" caseSensitive element={<CanonicalWascPath />} />
      <Route path="/WASC/*" element={<Deferred><WascSite /></Deferred>} />
      {IntakePreview ? (
        <Route path="/intake-preview" element={<Deferred><IntakePreview /></Deferred>} />
      ) : null}
      {CommunicationsPreview ? (
        <Route path="/communications-preview" element={<Deferred><CommunicationsPreview /></Deferred>} />
      ) : null}
      {NoticePeriodPreview ? (
        <Route path="/notice-period-preview" element={<Deferred><NoticePeriodPreview /></Deferred>} />
      ) : null}
      {NoticeMonthPreview ? (
        <Route path="/notice-month-preview" element={<Deferred><NoticeMonthPreview /></Deferred>} />
      ) : null}
      {WeekMeetsPreview ? (
        <Route path="/week-meets-preview" element={<Deferred><WeekMeetsPreview /></Deferred>} />
      ) : null}
      <Route path="/login" element={<Deferred><Login /></Deferred>} />
      <Route path="/auth/*" element={<Deferred><AuthRoutes /></Deferred>} />
      <Route path="/poolside" element={<Deferred><PoolsideRoutes /></Deferred>} />
      <Route path="/poolside/*" element={<Deferred><PoolsideRoutes /></Deferred>} />
      <Route path="/app" element={<Deferred><AdminRoutes /></Deferred>} />
      <Route path="/app/*" element={<Deferred><AdminRoutes /></Deferred>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
